import React from "react";
import { Box, Typography } from "@material-ui/core";
import { DateRangePicker, PickerType } from "halifax";
import dayjs from "dayjs";

import { MonthAndDatePickerConnectorProps } from "./container";
import "./styles.scss";
import clsx from "clsx";
import {
  AVAILABLE,
  getExperimentVariant,
  useExperiments,
  XMOD_V1_EXPERIMENT,
} from "../../../context/experiments";

export interface IMonthAndDatePickerProps
  extends MonthAndDatePickerConnectorProps {
  pickerType?: PickerType;
  viewType: MonthAndDatePickerType;
  header?: JSX.Element | string;
  subheader?: JSX.Element | string;
  // focusedMonthIndex must be given in order to make columnView focus on an arbitrary month
  focusedMonthIndex?: number;
  // setFocusedMonthIndex must be given for working with the groupView + columnView combination
  setFocusedMonthIndex?: (index: number) => void;
  setStartDate: (date: Date | null) => void;
  setEndDate: (date: Date | null) => void;
  startDate: Date | null;
  endDate: Date | null;
  minAllowedDate?: Date;
  maxAllowedDate?: Date;
  className?: string;
  unavailableDates?: Date[];
  pricePerDay?: { [date: string]: string };
}

export const MonthAndDatePicker = (props: IMonthAndDatePickerProps) => {
  const {
    pickerType = PickerType.RANGE,
    startDate,
    endDate,
    setStartDate,
    setEndDate,
    viewType,
    header,
    subheader,
    focusedMonthIndex,
    setFocusedMonthIndex,
    className,
    minAllowedDate,
    maxAllowedDate,
    unavailableDates,
    pricePerDay,
  } = props;

  const expState = useExperiments();
  const isXmodRedesign =
    getExperimentVariant(expState.experiments, XMOD_V1_EXPERIMENT) ===
    AVAILABLE;

  return (
    <>
      {header && (
        <Box
          className={clsx("month-and-date-picker-header-container", {
            "with-subheader": subheader,
          })}
        >
          {typeof header === "string" ? (
            <Typography variant="body2">{header}</Typography>
          ) : (
            header
          )}
        </Box>
      )}
      {subheader && (
        <Box className="month-and-date-picker-subheader-container">
          {typeof subheader === "string" ? (
            <Typography variant="body2">{subheader}</Typography>
          ) : (
            subheader
          )}
        </Box>
      )}
      <DateRangePicker
        pickerType={pickerType}
        className={clsx(className, { xmod: isXmodRedesign })}
        minAllowedDate={minAllowedDate || dayjs().toDate()}
        maxAllowedDate={maxAllowedDate || dayjs().add(1, "year").toDate()}
        months={[]}
        priceTags={[]}
        startDate={startDate}
        endDate={endDate}
        {...(viewType === MonthAndDatePickerType.Horizontal ||
        viewType === MonthAndDatePickerType.Column
          ? { setStartDate, setEndDate, pickerType }
          : undefined)}
        {...(viewType === MonthAndDatePickerType.Column
          ? { focusedMonthIndex, columnView: true }
          : undefined)}
        {...(viewType === MonthAndDatePickerType.Group
          ? { setFocusedMonthIndex, groupView: true }
          : undefined)}
        unavailableDates={unavailableDates}
        pricePerDay={pricePerDay}
      />
    </>
  );
};

export enum MonthAndDatePickerType {
  Horizontal,
  Column,
  Group,
}
